import { isDesktop } from '../../lib/platform.ts'

/**
 * Permissão de microfone e câmera perguntada pelo ORBIND (SPEC-20260916-2335).
 *
 * No desktop, o motor (Edge no Windows, WebKit no Linux) mostraria a janela
 * dele — cara de navegador, e sem memória entre sessões. O shell intercepta o
 * pedido e responde pela decisão guardada; quem pergunta é esta tela.
 *
 * Fluxo: antes de pedir a mídia, a chamada usa `garantirPermissao`. Sem
 * decisão guardada, o diálogo do Orbind aparece; o "sim" é gravado no shell e
 * só então `getUserMedia` é chamado — o motor encontra a resposta pronta e não
 * mostra janela nenhuma. No navegador nada disso vale: o pedido é do próprio
 * navegador, como sempre foi.
 */

export type TipoDePermissao = 'microfone' | 'camera'

type Invoke = (comando: string, argumentos?: Record<string, unknown>) => Promise<unknown>

function ponte(): Invoke | null {
  if (!isDesktop()) return null
  const w = window as unknown as { __TAURI_INTERNALS__?: { invoke?: Invoke } }
  return w.__TAURI_INTERNALS__?.invoke ?? null
}

const origemAtual = () => window.location.origin

/** Decisão guardada: `null` = ninguém respondeu ainda. */
export async function estadoDaPermissao(tipo: TipoDePermissao): Promise<boolean | null> {
  const invoke = ponte()
  if (!invoke) return true // navegador: quem pergunta é ele
  try {
    return ((await invoke('permissao_estado', { origem: origemAtual(), tipo })) as boolean | null) ?? null
  } catch {
    return null
  }
}

async function gravar(tipo: TipoDePermissao, permitir: boolean): Promise<void> {
  const invoke = ponte()
  if (!invoke) return
  try {
    await invoke('permissao_definir', { origem: origemAtual(), tipo, permitir })
  } catch {
    /* shell antigo sem o comando: segue com o diálogo do motor */
  }
}

export type DecisaoDePermissao = { origem: string; tipo: string; permitido: boolean }

/** O que já foi respondido — alimenta Preferências. */
export async function listarPermissoes(): Promise<DecisaoDePermissao[]> {
  const invoke = ponte()
  if (!invoke) return []
  try {
    return ((await invoke('permissoes_listar')) as DecisaoDePermissao[] | null) ?? []
  } catch {
    return []
  }
}

/** Esquece uma decisão: o Orbind volta a perguntar na próxima vez. */
export async function esquecerPermissao(origem: string, tipo: string): Promise<void> {
  const invoke = ponte()
  if (!invoke) return
  try {
    await invoke('permissao_esquecer', { origem, tipo })
  } catch {
    /* sem o comando: nada a esquecer */
  }
}

/* ------------------------------------------------------------------ pedido */

type Pedido = { tipo: TipoDePermissao; responder: (permitir: boolean) => void }
type Ouvinte = (pedido: Pedido | null) => void

let pedidoAberto: Pedido | null = null
const ouvintes = new Set<Ouvinte>()

/** O diálogo se inscreve aqui para saber quando perguntar. */
export function ouvirPedidoDePermissao(ouvinte: Ouvinte): () => void {
  ouvintes.add(ouvinte)
  ouvinte(pedidoAberto)
  return () => {
    ouvintes.delete(ouvinte)
  }
}

function abrir(tipo: TipoDePermissao): Promise<boolean> {
  // Um pedido por vez: o segundo espera o primeiro (microfone e depois câmera).
  return new Promise((resolve) => {
    pedidoAberto = {
      tipo,
      responder: (permitir) => {
        pedidoAberto = null
        for (const o of ouvintes) o(null)
        resolve(permitir)
      },
    }
    for (const o of ouvintes) o(pedidoAberto)
  })
}

/**
 * Garante a permissão ANTES de pedir a mídia. `true` = pode chamar
 * `getUserMedia`; `false` = a pessoa disse não (nem tenta, para não sobrar um
 * erro solto na tela).
 */
export async function garantirPermissao(tipo: TipoDePermissao): Promise<boolean> {
  if (!ponte()) return true
  const guardada = await estadoDaPermissao(tipo)
  if (guardada !== null) return guardada
  // sem ninguém para perguntar (diálogo não montado): não trava a chamada
  if (ouvintes.size === 0) return true
  const resposta = await abrir(tipo)
  await gravar(tipo, resposta)
  return resposta
}
